import React, { useState } from "react";
import { useUscSelector } from "../../../../../redux/store";
import type { VehicleType } from "../../../../../../shared-logic/types/UscCommonTypes";
import { UscContext } from "../../../../../../shared-logic/types/UscCommonTypes";
import { useUscLabel } from "../../../../../utils/labels";
import type { FormattedQuickSpecsType } from "../../../../../utils/types";
import type { UscSpecParamsSettingsType } from "../../../../../../shared-logic/utils/uscSpecUtils";
import { formatQuickSpecs } from "../../../../../../shared-logic/utils/uscSpecUtils";
import SectionDimensions from "./sections/SectionDimensions";
import SectionConsumption from "./sections/SectionConsumption";
import SectionEngine from "./sections/SectionEngine";
import { SectionPerformance } from "./sections/SectionPerformance";
import SectionTransmission from "./sections/SectionTransmission";
import SectionTyreInfo from "./sections/SectionTyreInfo";
import Disclosure from "../../../styles/molecules/Disclosure";
import Text from "../../../styles/atoms/Text";
import Icon from "../../../styles/Icon";
import * as CarDetailsStyles from "../CarDetails.styles";
import { useBreakpoint } from "../../../../../../../common-deprecated/themes/common";
import { trackEvent } from "../../../../../../../common-deprecated/utils/tracking";
import { getAemTrackingDefaults, UscTrackingLocation } from "../../../../../utils/tracking";
import type { SpecCategoryType } from "../../../../../../shared-logic/utils/uscUtils";
import { SpecSection } from "../../../../../utils/constants";
import { enableLegalEcoLabel } from "../../../../../utils/pdpUtils";
import { showSpecSection, uscSpecsParamsSettingsSelector } from "../../../../../utils/specUtils";

export type SpecsSectionPropsType = {
    product: VehicleType["product"];
    cultureName: string;
    title: string;
    specs: FormattedQuickSpecsType;
    settings?: UscSpecParamsSettingsType;
};

const aemToyotaSpecSectionComponentsMap: {
    [key in SpecSection]: React.FunctionComponent<SpecsSectionPropsType>;
} = {
    [SpecSection.Dimensions]: SectionDimensions,
    [SpecSection.Consumption]: SectionConsumption,
    [SpecSection.Engine]: SectionEngine,
    [SpecSection.Performance]: SectionPerformance,
    [SpecSection.Transmission]: SectionTransmission,
    [SpecSection.TyreInfo]: SectionTyreInfo,
};

type SpecSectionType = {
    category: SpecCategoryType;
    formattedSpecs: FormattedQuickSpecsType;
    SpecSectionComponent: (typeof aemToyotaSpecSectionComponentsMap)[keyof typeof aemToyotaSpecSectionComponentsMap];
};

const Specifications = (): JSX.Element | null => {
    const uscContext = useUscSelector((state) => state.vehicle.type);
    const product = useUscSelector((state) => state.vehicle.result?.product);
    const culture = useUscSelector((state) => state.commonSettings.culture);
    const country = useUscSelector((state) => state.commonSettings.country);
    const uscSettings = useUscSelector((state) => state.uscSettings);
    const specParamSettings = useUscSelector(uscSpecsParamsSettingsSelector);

    const [opened, setOpened] = useState<boolean>(false);

    const title = useUscLabel("uscPdpSpecTitle");

    const isMobile = useBreakpoint("down", "md");

    const specsSections = uscSettings.specCategories.reduce((sections, category) => {
        const SpecSectionComponent = aemToyotaSpecSectionComponentsMap[category.type];
        const specs = uscSettings.specCategories.find((cat) => cat.type === category.type)?.specs;

        const isValidSection = !!SpecSectionComponent && !!specs?.length && showSpecSection(category.type, product);
        if (isValidSection) {
            sections.push({ SpecSectionComponent, category, formattedSpecs: formatQuickSpecs(specs) });
        }

        return sections;
    }, [] as SpecSectionType[]);

    if (!specsSections.length) return null;

    const trackedToggle = (): void => {
        trackEvent({
            ...getAemTrackingDefaults(UscContext.Used, UscTrackingLocation.Pdp),
            action: `${opened ? "reduce" : "expand"}-section`,
            label: title,
        });
        setOpened(!opened);
    };

    return (
        <Disclosure variant="specs" ariaLabel={title}>
            <summary onClick={trackedToggle}>
                <div>
                    {!isMobile && <Icon variant="manual" iconSize="medium" />}
                    <Text tag="h3" variant="h6">
                        {title}
                    </Text>
                </div>
                <Icon variant="chevron-down" />
            </summary>
            <CarDetailsStyles.DisclosureBody>
                {specsSections.map(({ SpecSectionComponent, category, formattedSpecs }) => {
                    // For TDG SCL, we render all consumption related data in a new legal section, handled
                    // in LegalSpecifications.tsx. We therefore don't render the consumption section here.
                    if (
                        enableLegalEcoLabel(country, uscContext, uscSettings, true) &&
                        category.type === SpecSection.Consumption
                    ) {
                        return null;
                    }

                    return (
                        <SpecSectionComponent
                            key={category.type}
                            product={product!} // we know product exists because we checked it in showSpecSection
                            cultureName={culture.name}
                            title={category.title}
                            specs={formattedSpecs}
                            settings={specParamSettings}
                        />
                    );
                })}
            </CarDetailsStyles.DisclosureBody>
        </Disclosure>
    );
};

export default Specifications;
